import type { AgentMessageFragmentAction } from "@/hooks/use-stream-conversation";
import { useMemo, useState, type FC } from "react";
import { Button } from "@/components/flexkit/Button";
import { IconError } from "@/assets/icons/geist/IconError";
import { cn } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";
import { ScrollArea } from "@/components/flexkit/ScrollArea";

type FragmentActionProps = {
    action: AgentMessageFragmentAction;
};

export const FragmentActionShellScript: FC<FragmentActionProps> = ({ action }) => {
    return (
        <div
            className={cn("-mt-3 flex items-start gap-1 duration-700 animate-in fade-in", {
                "opacity-1 transition-none duration-0": action.state === "completed",
            })}
        >
            <div className="flex min-w-0 flex-grow flex-col">
                <FragmentActionShellScriptResult action={action} />
            </div>
        </div>
    );
};

const FragmentActionShellScriptResult: FC<{ action: AgentMessageFragmentAction }> = ({ action }) => {
    const [isResultExpanded, setIsResultExpanded] = useState(false);

    const { resultText, shouldTruncate, totalLines } = useMemo(() => {
        const lines = action.resultDescription.split("\n");
        const truncated = lines.length > 5;
        const displayLines = truncated && !isResultExpanded ? lines.slice(0, 5) : lines;

        return {
            resultText: displayLines.join("\n"),
            shouldTruncate: truncated,
            totalLines: lines.length,
        };
    }, [action.resultDescription, isResultExpanded]);

    return (
        <div className="flex flex-col overflow-hidden rounded-lg border border-border-base">
            <FragmentActionShellScriptHeader action={action} />
            <ScrollArea orientation="both" className="flex max-h-[300px] flex-col pb-0 pl-[3px] pt-[3px]">
                <pre className="grow p-2 font-mono text-sm text-content-secondary">
                    <code>
                        {action.state === "failed" ? (
                            <span className="flex items-start gap-1">
                                <IconError size="sm" className="shrink-0 text-content-destructive" /> {resultText}
                            </span>
                        ) : (
                            resultText
                        )}
                    </code>
                </pre>
            </ScrollArea>
            {shouldTruncate && (
                <div className="flex shrink-0 items-center justify-center p-1">
                    <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => setIsResultExpanded(!isResultExpanded)}
                        className="text-xs text-content-secondary"
                        data-tracking-id="expand-shell-result"
                    >
                        {isResultExpanded ? "Show less" : `Show ${totalLines - 5} more lines`}
                    </Button>
                </div>
            )}
        </div>
    );
};

const FragmentActionShellScriptHeader: FC<{
    action: AgentMessageFragmentAction;
}> = ({ action }) => {
    const text = useMemo(() => action.intentDescription.replace(/📟/g, "").trim(), [action.intentDescription]);
    return (
        <div className={cn("flex items-center gap-2 border-b border-border-base py-2")}>
            <Text className={cn("min-w-0 break-words pl-3 font-mono text-sm font-normal text-content-primary")}>
                $ {text}
            </Text>
        </div>
    );
};
